import { Component, OnInit, ChangeDetectorRef, Inject, ChangeDetectionStrategy, ViewChild } from "@angular/core";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef, MatTabGroup } from "@angular/material";
import { CoreLogService } from "../../services/log.service";
import { PagingHelper } from "../../shared/pagingHelper";
import { Paging } from "../../shared/paging";
import { SortOption } from "../../shared/models/sort.model";
import { SortDirection, SnackBarMessageType, WebShopMode, RedeemType } from "../../shared/contentTypes";
import { LocationViewModel, orderTypes, OrdertypeViewModel } from "../../shared/models";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { LocationService } from "../../components/location/location.service";
import { Observable } from "rxjs";
import { map } from "rxjs/internal/operators/map";
import { WebshopOrdertypesService } from "../../services/webShopOrdertypes.service";
import { IOSHelperService } from "../../services/IOSHelper.service";
import { CoreLanguageService } from "../../services/language.service";
import { WebShopService } from "../../services/webShop.service";

interface LocationAndOrdertypeDialogModalData {
  locationIds: number[];
  redeemType: RedeemType;
  /** Restrict shown order types to the nine-digit-compatible whitelist (retail/pickup/delivery). Single-redeemable coupons only. */
  onlyNineDigitOrderTypes?: boolean;
}

@Component({
  selector: "locationAndOrdertypeDialog",
  templateUrl: "./locationAndOrdertypeDialog.component.html",
  styleUrls: ["./locationAndOrdertypeDialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class LocationAndOrdertypeDialogComponent implements OnInit {

  public locationList: LocationViewModel[];
  public ordertypes$: Observable<OrdertypeViewModel[]>;

  private sortOption: SortOption[];
  private filterFunction: ((item: LocationViewModel) => boolean)[];
  public paging: Paging<LocationViewModel>;
  private pagingHelper: PagingHelper;

  public searchLocationKey: string;
  public isOrderTypeTabActive: boolean = false;
  public animateItemOnSearchFilter: boolean = false;
  private _selectedLocation: number;

  @ViewChild("matTabGroup") matTabGroup: MatTabGroup;

  constructor(public bottomSheetRef: MatBottomSheetRef<LocationAndOrdertypeDialogComponent>,
    private _locationService: LocationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _snackBarService: SnackBarService,
    private _webshopOrdertypesService: WebshopOrdertypesService,
    private _iosHelperService: IOSHelperService,
    private _coreLanguageService: CoreLanguageService,
    private _webShopService: WebShopService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public dialogData: LocationAndOrdertypeDialogModalData) {
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("SelectLocationAndOrdertypeDialogComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }


  ngOnInit() {
    const me = this;
    me.locationList = [];
    me.pagingHelper = new PagingHelper();
    me.sortOption = [];
    me.searchLocationKey = ""
    me.sortOption.push(me.pagingHelper.createSortOption("like", SortDirection.DESC));
    me.sortOption.push(me.pagingHelper.createSortOption("sort_nr", SortDirection.ASC));
    me.loadLocations();
    me.PushChanges();
  }

  /**set placeholder for search input */
  public setPlaceHolder() {
    const me = this;
    return me._coreLanguageService.value("phLocation_Search_Location");
  }

  /**set label for location tab */
  public get locationTabLabel() {
    const me = this;
    return me._coreLanguageService.value("phCoupon_Redeem_LocationAndOrderType_Dialog_Tab_location_Label");
  }

  /**set label for order type tab */
  public get orderTypeTabLabel() {
    const me = this;
    return me._coreLanguageService.value("phCoupon_Redeem_LocationAndOrderType_Dialog_Tab_OrderType_label");
  }

  public dialogTitle(redeemType: RedeemType): string {
    const me = this;
    let title = "";
    if (redeemType === RedeemType.Benefit) {
      title = "phBenefit_Redeem_LocationAndOrderType_Dialog_Title";
    } else { // RedeemType.Coupon 
      title = "phCoupon_Redeem_LocationAndOrderType_Dialog_Title";
    }
    return me._coreLanguageService.value(title);
  }

  public dialogDescription(redeemType: RedeemType): string {
    const me = this;
    let description = "";
    if (redeemType === RedeemType.Benefit) {
      description = "phBenefit_Redeem_LocationAndOrderType_Dialog_Tab_Location_Desc";
    } else { // RedeemType.Coupon
      description = "phCoupon_Redeem_LocationAndOrderType_Dialog_Tab_Location_Desc";
    }
    return me._coreLanguageService.value(description);
  }


  public dialogTabDescription(redeemType: RedeemType): string {
    const me = this;
    let tabDescription = "";
    if (redeemType === RedeemType.Benefit) {
      tabDescription = "phBenefit_Redeem_LocationAndOrderType_Dialog_Tab_Location_Desc";
    } else { // RedeemType.Coupon
      tabDescription = "phCoupon_Redeem_LocationAndOrderType_Dialog_Tab_OrderType_Desc";
    }
    return me._coreLanguageService.value(tabDescription);
  }

  public dialogEmptyTabDescription(redeemType: RedeemType): string {
    const me = this;
    let emptyTabDescription = "";
    if (redeemType === RedeemType.Benefit) {
      emptyTabDescription = "phBenefit_Redeem_LocationAndOrderType_Dialog_Tab_OrderType_Empty";
    } else { // RedeemType.Coupon
      emptyTabDescription = "phCoupon_Redeem_LocationAndOrderType_Dialog_Tab_OrderType_Empty";
    }
    return me._coreLanguageService.value(emptyTabDescription);
  }

  /**checks whether the image is available for the location*/
  public getHasImage(locationImage: string): boolean {
    return locationImage !== "" ? true : false;
  }

  /** loads the list of location
   * filters by available locations
   */
  private async loadLocations(): Promise<void> {
    const me = this;
    me._locationService.GetLocationViewModel()
      .then(locations => {
        if (locations) {
          locations = locations.filter(location =>
            me.dialogData.locationIds.includes(location.hyp_cus_nr)
          );
          const DefaultPageSize = locations.length;
          me.paging = new Paging(locations, DefaultPageSize, me.sortOption, me.filterFunction);
          me.locationList = []; // clean up
          me.locationList.push(...me.paging.initLoad());
          me.PushChanges();
        } else {
          me._snackBarService.ShowMessage(snackBarMessageKey.locationNotFound, SnackBarMessageType.Error);
        }
      }).catch(e => {
        me._coreLogService.error("SelectLocationAndOrdertypeDialogComponent.loadLocations", "GetLocationViewModel", e);
        me._snackBarService.ShowErrorMessageFailed(112);
      });
  }
  /**
 * This function filters LocationList when KeyUp Event of searchBox fired.
 * @param searchKey key for filtering Locationlist
 */
  public searchLocationFilter(event, searchKey: string): void {
    const me = this;
    if (event && event.keyCode === 13) {
      document.getElementById("locationSearcInput").blur();
      return;
    }
    me.filterFunction = [];
    me.searchLocationKey = searchKey;
    if (me.searchLocationKey.length > 1) {
      me.filterFunction.push(LocationAndOrdertypeDialogComponent.locationLabelFilterFunction(me.searchLocationKey));
    }
    me.paging.setFilter(me.filterFunction);
    me.locationList = []; // clean up
    me.locationList.push(...me.paging.initLoad());
    if (me.locationList.length === 0) {
      me.PushChanges();
      me._snackBarService.ShowMessage(snackBarMessageKey.locationNotFound, SnackBarMessageType.Warning);
    }
    me.animateItemOnSearchFilter = true;
    me.PushChanges();
  }

  /**
   * Returns order types for a location; applies a nine-digit whitelist when the flag is set.
   * Without the flag the existing blacklist applies unchanged (inHouse always excluded).
   */
  private getSupportServices$(hyp_cus_nr: number): Observable<OrdertypeViewModel[]> {
    const me = this;
    const onlyNineDigit = me.dialogData.onlyNineDigitOrderTypes === true;
    return me._webshopOrdertypesService.supportedOrdertypes$.pipe(
      map(ordertypes => ordertypes.filter(ordertype => me.isOrderTypeAllowed(ordertype, onlyNineDigit, hyp_cus_nr)))
    );
  }

  /**
   * Pure per-ordertype decision: returns true when the ordertype should appear in the list for the given location.
   * Inlined into getSupportServices$ filter; extracted here so specs can drive the real predicate.
   */
  public isOrderTypeAllowed(ordertype: OrdertypeViewModel, onlyNineDigitOrderTypes: boolean, hyp_cus_nr: number): boolean {
    const me = this;
    const orderModeViewing = 0;
    if (!ordertype.supported) { return false; }
    if (!me.filterDelivery(ordertype.id)) { return false; }
    if (ordertype.id === orderTypes.webvoucher) { return false; }
    if (ordertype.id === orderTypes.deliveryGroup) { return false; }
    if (ordertype.id === orderTypes.pickupGroup) { return false; }
    if (ordertype.id === orderTypes.ticket) { return false; }
    if (ordertype.id === orderModeViewing) { return false; }
    if (ordertype.id === orderTypes.inHouse) { return false; }
    if (!ordertype.locationIDs.includes(hyp_cus_nr)) { return false; }
    if (onlyNineDigitOrderTypes) {
      return ordertype.id === orderTypes.retail
        || ordertype.id === orderTypes.pickup
        || ordertype.id === orderTypes.delivery;
    }
    return true;
  }

  private _hasExternalLink: boolean;
  private async hasExternalLink(selectedLocationID: number) {
    const me = this;
    return me._hasExternalLink = await me._webShopService.hasExternalLink(selectedLocationID);
  }

  private filterDelivery(ordertypeId: number) {
    const me = this;
    if (me._hasExternalLink && ordertypeId === orderTypes.delivery) {
      return ordertypeId !== orderTypes.delivery
    } else {
      return ordertypeId;
    }
  }

  /**
   * set Requirements and pass result
   * @param servicemode 
   */
  public setRequirementsAndClose(servicemode: WebShopMode) {
    const me = this;
    const result = {
      selectedLocation: me._selectedLocation,
      selectedServiceMode: servicemode
    }
    me.bottomSheetRef.dismiss(result);
  }

  /**
   * sets selected location
   * loads ordertypes
   * changes the selected index of the mat tab.
   * @param location 
   */
  public async selectLocation(location: LocationViewModel) {
    const me = this;
    me._selectedLocation = location.hyp_cus_nr;
    if (me._selectedLocation) {
      me.isOrderTypeTabActive = true;
      await me.hasExternalLink(me._selectedLocation);
      me.loadOrderTypes(me._selectedLocation)
      me.matTabGroup.selectedIndex = 1;
      me.PushChanges();
    }
  }

  /**
   * loads the list of order types
   * @param selectedLocation 
   */
  private loadOrderTypes(selectedLocation: number): void {
    const me = this;
    me.ordertypes$ = me.getSupportServices$(selectedLocation);
    me.PushChanges();
  }

  /**
 * This function filters the locationList base on the SearchKey in the SearchBox at the top of the page.
 * @param searchKey used for filtering the data. Location name will be filtered by searchKey.
 * @result return a function that is used in paging Class to filter data.
 */
  private static locationLabelFilterFunction(searchKey: string): ((item: LocationViewModel) => boolean) {
    return (location: LocationViewModel): boolean => {
      return (location.label.toLowerCase().includes(searchKey.toLowerCase())
        || location.street.toLowerCase().includes(searchKey.toLowerCase())
        || location.location.toLowerCase().includes(searchKey.toLowerCase())
        || location.zipCode.toLowerCase().includes(searchKey.toLowerCase())
      );
    };
  }

  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

}
